import React, { useState, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Divider,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AddCircleOutline } from "@mui/icons-material";
import { Search } from "lucide-react";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { EditIcon } from "../comps/CustomIcons";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import { MASTER_POLICY_DATA } from "../_mock/mock";
import { useNavigate } from "react-router-dom";

const searchByOptions = [
  { value: "name", label: "Name" },
  { value: "scheme", label: "Scheme" },
];

export default function MasterPolicy() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("name");
  const [selectedRow, setSelectedRow] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT);
  const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);
  const [modalOpen, setModalOpen] = useState(false);

  const searchByLabel =
    searchByOptions.find((o) => o.value === searchBy)?.label || "Name";

  const handleModalOpen = () => setModalOpen(true);
  const handleAddItem = () => navigate("add");
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };
  const handleEdit = (rowData) => {
    setSelectedRow(rowData);
    handleModalOpen();
  };

  const rawRows = (MASTER_POLICY_DATA || []).map((row, idx) => ({
    id: row.id ?? row._id ?? idx,
    _id: row.id ?? row._id,
    name: row?.name || "-",
    scheme: row?.scheme || "-",
    status:
      row?.active === true || row?.active === "Active" ? "Active" : "Inactive",
    _original: row,
  }));

  const searchLower = String(search || "").toLowerCase();
  const filteredRows = searchLower
    ? rawRows.filter((r) =>
        String(r[searchBy] ?? "").toLowerCase().includes(searchLower)
      )
    : rawRows;

  const sortField = sortModel[0]?.field || "name";
  const sortOrder = sortModel[0]?.sort || "asc";
  const sortedRows = [...filteredRows].sort((a, b) => {
    const cmp = String(a[sortField] ?? "").localeCompare(
      String(b[sortField] ?? ""),
      undefined,
      { numeric: true }
    );
    return sortOrder === "asc" ? cmp : -cmp;
  });
  const totalFiltered = sortedRows.length;
  const rows =
    totalFiltered > rowsPerPage
      ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : sortedRows;

  const getColumns = useCallback(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 120,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "scheme",
        headerName: "Scheme",
        flex: 1,
        minWidth: 120,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        minWidth: 90,
        headerAlign: "center",
        align: "center",
        sortable: true,
        renderCell: (params) => (
          <Chip
            label={params.value || "Active"}
            color={params.value === "Inactive" ? "error" : "success"}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0,
        width: 100,
        minWidth: 90,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
            <IconButton
              size="small"
              onClick={() => handleEdit(params.row._original || params.row)}
            >
              <EditIcon
                sx={{
                  cursor: "pointer",
                  color: "transparent",
                  fontSize: "18px",
                }}
              />
            </IconButton>
            <IconButton size="small">
              <RemoveRedEyeOutlinedIcon sx={{ fontSize: "18px" }} />
            </IconButton>
          </Box>
        ),
      },
    ],
    []
  );

  const dataGridSx = {
    border: "none",
    "& .MuiDataGrid-root": { border: "none", width: "100%" },
    "& .MuiDataGrid-cell": {
      borderBottom: "1px solid #f0f0f0",
      padding: isMobile ? "4px" : "8px",
      fontSize: isMobile ? "12px" : "14px",
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#f5f5f5",
      borderBottom: "2px solid #e0e0e0",
    },
    "& .MuiDataGrid-columnHeader": { backgroundColor: "#f5f5f5", fontWeight: "bold" },
    "& .MuiDataGrid-row": {
      "&:hover": { backgroundColor: "#f5f5f5" },
      "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
      "&:nth-of-type(even)": { backgroundColor: "white" },
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "2px solid #e0e0e0",
      backgroundColor: "#f9f9f9",
    },
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px", paddingBottom: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: "#5D6A82",
              textTransform: "none",
              fontSize: "16px",
              fontWeight: "bold",
              padding: 0,
              transition: "none !important",
              mt: 2,
            }}
          >
            Back
          </Button>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: 2,
            }}
          >
            <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
              Policy Master
            </Typography>
          </Box>
          <Box
            sx={{
              my: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 2,
            }}
          >
            <TextField
              placeholder={`Search by ${searchByLabel}`}
              size="small"
              sx={{ width: "300px" }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <select
                        className="uid-select"
                        value={searchBy}
                        onChange={(e) => setSearchBy(e.target.value)}
                        style={{
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          cursor: "pointer",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {searchByOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
              value={search || ""}
              onChange={(e) => setSearch(e.target.value || "")}
            />
            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: "150px",
              }}
              onClick={handleAddItem}
            >
              <AddCircleOutline />
              <Typography sx={{ color: "white" }}>New Policy</Typography>
            </Button>
          </Box>

          <Box
            sx={{
              height: {
                xs: "calc(100vh - 300px)",
                sm: "calc(100vh - 320px)",
                md: "calc(100vh - 350px)",
              },
              width: "100%",
              minHeight: "300px",
              overflow: "hidden",
            }}
          >
            <DataGrid
              rows={rows}
              columns={getColumns()}
              paginationModel={{ page, pageSize: rowsPerPage }}
              onPaginationModelChange={(m) => {
                setPage(m.page);
                setRowsPerPage(m.pageSize);
              }}
              pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
              rowCount={totalFiltered}
              sortModel={sortModel}
              onSortModelChange={(m) => setSortModel(m)}
              disableRowSelectionOnClick
              sx={dataGridSx}
              components={{
                NoRowsOverlay: () => (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No policies found
                    </Typography>
                  </Box>
                ),
              }}
            />
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
